import { useEffect, useState } from 'react';
import { api, transitionLabel, LEGACY_TRANSITIONS, LAYER_POSITIONS,
         type Asset, type Brand, type CardDefaults as Defaults, type LayerPosition, type Sourcing, type Mode } from '../lib/api';

// ---------------------------------------------------------------------------
// How a new card starts.
//
// A creator making their fortieth video for the same channel sets the same
// backdrop, the same bed, the same transition and the same recurring theme
// forty times. The brand already holds what should not change between videos —
// its palette, its voices, its shelf of transitions. This is the rest of it.
//
// COPIED onto a card at creation, never referenced. A card is the record of a
// video made a particular way, and a default that reached through to it would
// silently re-cut last month's videos the day somebody changed the backdrop.
// The panel says so, because "will this change my old videos" is the first
// thing anybody sensible wonders.
// ---------------------------------------------------------------------------

/** The defaults as the API takes them: empty strings become absent keys. */
function cleanDefaults(d: Defaults): Defaults {
  return {
    ...(d.logo?.assetId ? { logo: d.logo } : {}),
    ...(d.topic?.trim() ? { topic: d.topic.trim() } : {}),
    ...(d.painPoint?.trim() ? { painPoint: d.painPoint.trim() } : {}),
    ...(d.speakers?.length ? { speakers: d.speakers } : {}),
    ...(d.rig?.wordmark ? { rig: d.rig } : {}),
    ...(d.mix && Object.values(d.mix).some(Boolean) ? { mix: d.mix } : {}),
  };
}

/** 190: the three sourcings as a creator reads them. */
export const SOURCING_LABEL: Record<Sourcing, string> = {
  library: 'from the library',
  collection: 'only this episode\'s collection',
  fresh: 'from scratch',
};

export function CardDefaults({ brand, onSaved }: {
  brand: Brand;
  onSaved: (b: Brand) => void;
}) {
  const [d, setD] = useState<Defaults>(brand.card_defaults ?? {});
  const [audio, setAudio] = useState<Asset[]>([]);
  const [images, setImages] = useState<Asset[]>([]);
  const [busy, setBusy] = useState(false);
  const [presetLabel, setPresetLabel] = useState('');
  // 190: what a card that starts from the preset lets the director draw from, and how it is made.
  const [presetSourcing, setPresetSourcing] = useState<Sourcing>('library');
  const [presetMode, setPresetMode] = useState<Mode>('directed');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // A set from the theme (F1): the picture model makes a broadcast-set
  // background from the recurring theme; it lands in the library and becomes
  // the default backdrop at a light dim, so the set reads in the gutters and
  // the words still read over it.
  const [making, setMaking] = useState(false);
  const [setNote, setSetNote] = useState<string | null>(null);
  async function makeSet() {
    if (!d.topic?.trim()) { setError('Give the theme first — the set is made from it.'); return; }
    setMaking(true); setError(null); setSetNote(null);
    try {
      const prompt = `A broadcast studio set background for a show about ${d.topic.trim()}: a wide, softly lit designed backdrop with depth — panels, glass, warm and cool light — no people, no text or lettering, calm enough to sit behind speakers, 16:9.`;
      const a = await api.generateAsset(prompt, brand.id);
      patch({ ...d, mix: { ...d.mix, background: { assetId: a.id, dim: 0.25 } } });
      setSetNote(`Set made — "${a.name ?? 'the set'}". It is promoting; save the defaults once it is ready.`);
      // It joins the backdrop list once ready; refresh the pictures shortly.
      setTimeout(() => {
        api.assets(brand.id).then((all) => setImages(all.filter((x) => x.status === 'ready' && (x.kind === 'image' || x.kind === 'video')))).catch(() => undefined);
      }, 8000);
    } catch (e) { setError(e instanceof Error ? e.message : 'could not make a set'); }
    finally { setMaking(false); }
  }

  useEffect(() => { setD(brand.card_defaults ?? {}); setSaved(false); }, [brand.id, brand.card_defaults]);

  useEffect(() => {
    // Only READY assets. One still promoting has no media key, so the API
    // would skip it when the default was applied — offering it here would be
    // offering a setting that silently does nothing.
    api.assets(brand.id).then((all) => {
      const ready = all.filter((a) => a.status === 'ready');
      setAudio(ready.filter((a) => a.kind === 'audio'));
      setImages(ready.filter((a) => a.kind === 'image' || a.kind === 'video'));
    }).catch(() => { setAudio([]); setImages([]); });
  }, [brand.id]);

  const shelf = brand.shelf_transitions ?? [...LEGACY_TRANSITIONS];

  function patch(next: Defaults) { setD(next); setSaved(false); }

  // The name a person gave it, never the key under which it is stored.
  function assetLabel(a: Asset): string {
    return a.name?.trim() || a.storage_key.split('/').pop() || a.id;
  }

  return (
    <section className="kit" aria-labelledby="kit-defaults">
      <header className="kit-hd">
        <h3 id="kit-defaults">How a new card starts</h3>
        <p>
          Copied onto a card when it is created on this brand. Changing anything here never
          alters a video you have already made.
        </p>
      </header>

      <div className="kit-bd">
        <div className="kit-form">
          <label className="field">
            Recurring theme
            <input
              value={d.topic ?? ''}
              placeholder="e.g. Listing photography for agents"
              onChange={(e) => patch({ ...d, topic: e.target.value || undefined })}
            />
          </label>
          <label className="field">
            The problem, concretely
            <input
              value={d.painPoint ?? ''}
              placeholder="e.g. Photos shot at the wrong hour cost showings"
              onChange={(e) => patch({ ...d, painPoint: e.target.value || undefined })}
            />
          </label>
          <label className="field">
            Between turns
            <select
              value={d.mix?.transition?.kind ?? ''}
              onChange={(e) => patch({
                ...d,
                mix: { ...d.mix, transition: e.target.value ? { kind: e.target.value } : undefined },
              })}
            >
              <option value="">no default</option>
              {shelf.map((k) => <option key={k} value={k}>{transitionLabel(k)}</option>)}
            </select>
          </label>
          <label className="field">
            Speakers as
            <select value={d.mix?.panels ?? 'card'}
                    onChange={(e) => patch({ ...d, mix: { ...d.mix, panels: e.target.value === 'box' ? 'box' : undefined } })}>
              <option value="card">cards — drawn panels</option>
              <option value="box">boxes — full-bleed faces, thin border, name band</option>
            </select>
          </label>
          {d.mix?.panels === 'box' && (
            <label className="field">
              Box line · width
              <span className="row" style={{ gap: 6 }}>
                <select value={d.mix?.box?.line ?? 'light'}
                        onChange={(e) => patch({ ...d, mix: { ...d.mix, box: { ...d.mix?.box, line: e.target.value } } })}>
                  <option value="light">light</option>
                  <option value="accent">accent</option>
                  <option value="#ffffff">white</option>
                  <option value="#000000">black</option>
                </select>
                <input type="number" min={2} max={10} step={1} style={{ width: 56 }} value={d.mix?.box?.width ?? 5}
                       onChange={(e) => patch({ ...d, mix: { ...d.mix, box: { ...d.mix?.box, width: Math.max(2, Math.min(10, Number(e.target.value) || 5)) } } })} />
              </span>
            </label>
          )}
          <label className="field" style={{ alignSelf: 'end' }}>
            <span><input type="checkbox" checked={Boolean(d.rig?.wordmark)}
                         onChange={(e) => patch({ ...d, rig: e.target.checked ? { wordmark: true } : undefined })} />
              {' '}the wordmark on every lower third</span>
            <span className="sub-dim">The rig's fixed marks: the show's first logo in the band's left cell, “{brand.wordmark || brand.name}” in its right cell.</span>
          </label>
          <label className="field">
            Music bed
            <select
              value={d.mix?.music?.assetId ?? ''}
              onChange={(e) => patch({
                ...d,
                mix: { ...d.mix, music: e.target.value ? { assetId: e.target.value } : undefined },
              })}
            >
              <option value="">no default</option>
              {audio.map((a) => <option key={a.id} value={a.id}>{assetLabel(a)}</option>)}
            </select>
          </label>
          <label className="field">
            The show&apos;s mark
            <select
              value={d.logo?.assetId ?? ''}
              onChange={(e) => patch({ ...d, logo: e.target.value ? { ...(d.logo ?? {}), assetId: e.target.value } : undefined })}
            >
              <option value="">no mark</option>
              {images.map((a) => <option key={a.id} value={a.id}>{assetLabel(a)}</option>)}
            </select>
            <small>Kept in a corner for the whole programme, on every card that starts here.</small>
          </label>
          <label className="field">
            Mark corner · height
            <span className="row" style={{ gap: 6 }}>
              <select value={d.logo?.position ?? 'bottom-left'} disabled={!d.logo}
                      onChange={(e) => patch({ ...d, logo: d.logo ? { ...d.logo, position: e.target.value as LayerPosition } : undefined })}>
                {LAYER_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <input type="number" min={40} max={400} step={10} value={d.logo?.height ?? 120} disabled={!d.logo}
                     style={{ width: 90 }}
                     onChange={(e) => patch({ ...d, logo: d.logo ? { ...d.logo, height: Math.max(40, Math.min(400, Number(e.target.value) || 120)) } : undefined })} />
            </span>
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            A set from the theme
            <span className="row" style={{ gap: 8, alignItems: 'center' }}>
              <button type="button" className="btn" disabled={making || busy} onClick={() => void makeSet()}>
                {making ? 'Making the set…' : 'Make a set from the theme'}
              </button>
              <small>{setNote ?? 'The picture model draws a broadcast-set background from the theme and makes it the default backdrop, lightly dimmed.'}</small>
            </span>
          </label>
          <label className="field">
            Backdrop
            <select
              value={d.mix?.background?.assetId ?? ''}
              onChange={(e) => patch({
                ...d,
                mix: { ...d.mix, background: e.target.value ? { assetId: e.target.value } : undefined },
              })}
            >
              <option value="">no default</option>
              {images.map((a) => <option key={a.id} value={a.id}>{assetLabel(a)}</option>)}
            </select>
          </label>
        </div>
      </div>

      {(brand.presets ?? []).length > 0 && (
        <div className="kit-bd" style={{ borderTop: '1px solid var(--line)' }}>
          <h4 style={{ margin: '8px 0 4px' }}>Presets</h4>
          <p className="sub-dim" style={{ marginTop: 0 }}>Named starts for a new card. New card on the Flight Deck offers them.</p>
          {(brand.presets ?? []).map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
              <span>{p.label}</span>
              <span className="sub-dim">{[p.defaults.topic, p.defaults.speakers?.join(', '), p.defaults.mix?.panels === 'box' ? 'boxes' : null, p.defaults.rig?.wordmark ? 'wordmark' : null, p.sourcing && p.sourcing !== 'library' ? SOURCING_LABEL[p.sourcing] : null, p.mode === 'one-shot' ? 'one shot' : null].filter(Boolean).join(' · ')}</span>
              <span style={{ flex: 1 }} />
              <button type="button" className="btn tiny" disabled={busy} onClick={() => patch({ ...p.defaults })} title="Load these defaults into the form">load</button>
              <button type="button" className="btn tiny" disabled={busy} title="Remove the preset (cards made from it are unchanged)"
                      onClick={() => void api.removeCardPreset(brand.id, p.id).then((presets) => onSaved({ ...brand, presets })).catch((e) => setError(e instanceof Error ? e.message : 'could not remove'))}>×</button>
            </div>
          ))}
        </div>
      )}
      <footer className="kit-ft">
        <button
          className="btn pri"
          disabled={busy}
          onClick={() => {
            setBusy(true); setError(null);
            // Empty strings are stripped on the way out: the API bounds every
            // field at one character, so a cleared box has to become an absent
            // key rather than "".
            const clean = cleanDefaults(d);
            api.saveBrandKit(brand.id, { cardDefaults: clean })
              .then((b) => { onSaved(b); setSaved(true); })
              .catch((e) => setError(e instanceof Error ? e.message : 'could not save that'))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Saving…' : 'Save defaults'}
        </button>
        <button className="btn" disabled={busy} onClick={() => patch({})}>Clear</button>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 12 }}>
          <input value={presetLabel} maxLength={60} placeholder="Save these as a preset — e.g. Weekly episode"
                 onChange={(e) => setPresetLabel(e.target.value)} style={{ minWidth: 220 }} />
          <select value={presetSourcing} onChange={(e) => setPresetSourcing(e.target.value as Sourcing)} title="190: where the director may draw from on a card that starts here">
            {(Object.keys(SOURCING_LABEL) as Sourcing[]).map((k) => <option key={k} value={k}>{SOURCING_LABEL[k]}</option>)}
          </select>
          <select value={presetMode} onChange={(e) => setPresetMode(e.target.value as Mode)} title="190: made in one shot, or step by step through the phases">
            <option value="directed">step by step</option>
            <option value="one-shot">one shot</option>
          </select>
          <button className="btn" disabled={busy || !presetLabel.trim()}
                  title="E4: keep these defaults under a name; New card can start from it"
                  onClick={() => {
                    setBusy(true); setError(null);
                    api.addCardPreset(brand.id, { label: presetLabel.trim(), defaults: cleanDefaults(d), sourcing: presetSourcing, mode: presetMode })
                      .then((r) => { onSaved({ ...brand, presets: r.presets }); setPresetLabel(''); })
                      .catch((e) => setError(e instanceof Error ? e.message : 'could not save the preset'))
                      .finally(() => setBusy(false));
                  }}>Save as a preset</button>
        </span>
        {saved && <span className="ok">Saved — the next card starts this way.</span>}
        {error && <span className="err inline">{error}</span>}
      </footer>
    </section>
  );
}
